import React, { useState } from "react";
import {
  Accessibility,
  Building2,
  Car,
  Check,
  ChevronRight,
  Clock,
  CreditCard,
  Crown,
  Globe,
  Heart,
  HeartHandshake,
  MapPin,
  Music,
  Quote,
  ShieldCheck,
  Shirt,
  Sparkles,
  Star,
  Users,
  Wallet,
  Wand2,
  Wine,
  X,
  Scale,
} from "lucide-react";
import Placeholder from "./Placeholder";
import Reveal from "./Reveal";
import { useBooking } from "./BookingDialog";
import { Dialog, DialogContent } from "./ui/dialog";

const ICONS = {
  Accessibility,
  Building2,
  Car,
  Clock,
  CreditCard,
  Crown,
  Globe,
  Heart,
  HeartHandshake,
  MapPin,
  Music,
  ShieldCheck,
  Shirt,
  Sparkles,
  Star,
  Users,
  Wallet,
  Wand2,
  Wine,
};

export const Icon = ({ name, className = "h-5 w-5" }) => {
  const Cmp = ICONS[name] || Sparkles;
  return <Cmp className={className} />;
};

/* ---------------------------------- Gallery --------------------------------- */

const Gallery = ({ items }) => {
  const [active, setActive] = useState(null);
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it, i) => (
          <Reveal key={`${it.title}-${i}`} delay={(i % 3) * 90}>
            <button
              type="button"
              onClick={() => setActive(it)}
              className="luxury-card group block w-full overflow-hidden p-0 text-left"
            >
              <div className="overflow-hidden">
                <div className="transition-transform duration-700 group-hover:scale-[1.04]">
                  <Placeholder
                    src={it.img}
                    label={it.title}
                    ratio="4/3"
                    className="rounded-none border-0"
                  />
                </div>
              </div>
              {it.title || it.caption ? (
                <div className="p-5">
                  {it.title ? (
                    <h4 className="font-serif text-lg text-foreground transition-colors group-hover:text-primary">
                      {it.title}
                    </h4>
                  ) : null}
                  {it.caption ? (
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {it.caption}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </button>
          </Reveal>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl border-border/60 bg-popover p-0">
          {active && (
            <div>
              <Placeholder
                src={active.img}
                label={active.title}
                ratio="16/9"
                className="rounded-b-none border-0"
              />
              {active.title || active.caption ? (
                <div className="p-6">
                  {active.title ? (
                    <h4 className="font-serif text-2xl text-foreground">{active.title}</h4>
                  ) : null}
                  {active.caption ? (
                    <p className="mt-3 leading-relaxed text-muted-foreground">{active.caption}</p>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ---------------------------------- Rates ---------------------------------- */

const RatesTable = ({ rows, note, venueName }) => {
  const { open } = useBooking();
  return (
    <div>
      <div className="luxury-card overflow-hidden">
        <div className="hidden grid-cols-[1.5fr_0.8fr_2fr_0.9fr_0.9fr_auto] gap-4 border-b border-border/60 bg-muted/40 px-6 py-4 text-[11px] uppercase tracking-[0.18em] text-muted-foreground lg:grid">
          <span>Duration</span>
          <span>Total</span>
          <span>Description</span>
          <span>Ladies</span>
          <span>Room</span>
          <span className="text-right">Book</span>
        </div>

        {rows.map((r, i) => (
          <div
            key={r.duration}
            className={`group grid grid-cols-1 gap-3 px-6 py-5 transition-colors hover:bg-primary/[0.06] lg:grid-cols-[1.5fr_0.8fr_2fr_0.9fr_0.9fr_auto] lg:items-center lg:gap-4 ${
              i !== rows.length - 1 ? "border-b border-border/50" : ""
            }`}
          >
            <span className="font-serif text-base text-foreground">{r.duration}</span>
            <span className="text-lg font-semibold text-primary">{r.total}</span>
            <span className="text-sm leading-relaxed text-muted-foreground">{r.desc}</span>
            <span className="text-sm text-muted-foreground">
              <span className="lg:hidden">Ladies: </span>
              {r.ladies}
            </span>
            <span className="text-sm text-muted-foreground">
              <span className="lg:hidden">Room: </span>
              {r.room}
            </span>
            <button
              onClick={() => open({ venue: venueName, duration: r.duration })}
              className="mt-2 inline-flex items-center justify-center gap-1 rounded-md border border-primary/40 px-4 py-2 text-xs font-medium uppercase tracking-wider text-foreground transition-colors hover:bg-primary hover:text-primary-foreground lg:mt-0"
            >
              Book now <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      {note ? <p className="mt-4 text-xs italic text-muted-foreground">{note}</p> : null}
    </div>
  );
};

const RatesCustom = ({ title, columns, rows }) => (
  <div className="luxury-card overflow-hidden">
    {title ? (
      <div className="border-b border-border/60 bg-muted/40 px-6 py-4">
        <h4 className="text-[11px] uppercase tracking-[0.28em] text-primary">{title}</h4>
      </div>
    ) : null}
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-left">
        <thead>
          <tr className="border-b border-border/50">
            {columns.map((c) => (
              <th
                key={c}
                className="px-6 py-3 text-[11px] uppercase tracking-[0.16em] text-muted-foreground"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`transition-colors hover:bg-primary/[0.06] ${
                i !== rows.length - 1 ? "border-b border-border/40" : ""
              }`}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={`px-6 py-4 text-sm ${
                    j === 0
                      ? "font-serif text-foreground"
                      : j === row.length - 1
                      ? "font-semibold text-primary"
                      : "text-muted-foreground"
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/* --------------------------------- Reviews --------------------------------- */

export const Reviews = ({ items }) => (
  <div className="grid gap-6 md:grid-cols-2">
    {items.map((r, i) => (
      <Reveal key={`${r.name}-${i}`} delay={(i % 2) * 90}>
        <figure className="luxury-card h-full overflow-hidden">
          {r.img ? (
            <div className="border-b border-border/50 bg-muted/20">
              <img
                src={r.img}
                alt={`${r.name} review`}
                loading="lazy"
                className="max-h-56 w-full object-contain"
              />
            </div>
          ) : null}
          <div className="p-7">
            <Quote className="h-6 w-6 text-primary/70" />
            <blockquote className="mt-4 text-sm leading-relaxed text-muted-foreground">
              {r.text}
            </blockquote>
            <figcaption className="mt-6 flex items-center gap-3 border-t border-border/50 pt-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 font-serif text-sm text-primary">
                {r.name.charAt(0)}
              </span>
              <span>
                <span className="block font-serif text-sm text-foreground">{r.name}</span>
                <span className="flex gap-0.5 pt-1">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="h-3 w-3 fill-primary text-primary" />
                  ))}
                </span>
              </span>
            </figcaption>
          </div>
        </figure>
      </Reveal>
    ))}
  </div>
);

/* ------------------------------ Block renderer ----------------------------- */

const Block = ({ block, venueName }) => {
  switch (block.t) {
    case "lead":
      return (
        <Reveal>
          <p className="text-lg leading-relaxed text-foreground/90 sm:text-xl">{block.text}</p>
        </Reveal>
      );
    case "p":
      return (
        <Reveal>
          <p className="leading-relaxed text-muted-foreground">{block.text}</p>
        </Reveal>
      );
    case "h3":
      return (
        <Reveal>
          <h3 className="pt-4 font-serif text-2xl text-foreground sm:text-3xl">{block.text}</h3>
        </Reveal>
      );
    case "h4":
      return (
        <Reveal>
          <h4 className="pt-2 font-serif text-xl text-accent">{block.text}</h4>
        </Reveal>
      );
    case "ul":
      return (
        <Reveal>
          <ul className="grid gap-3 sm:grid-cols-2">
            {block.items.map((it) => (
              <li key={it} className="flex items-start gap-3">
                <span className="mt-1 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-primary/15">
                  <Check className="h-2.5 w-2.5 text-primary" />
                </span>
                <span className="text-sm leading-relaxed text-muted-foreground">{it}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      );
    case "tags":
      return (
        <Reveal>
          <div className="flex flex-wrap gap-2.5">
            {block.items.map((it) => (
              <span
                key={it}
                className="rounded-full border border-border/60 bg-muted/50 px-4 py-2 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
              >
                {it}
              </span>
            ))}
          </div>
        </Reveal>
      );
    case "note":
      return (
        <Reveal>
          <p className="rounded-lg border-l-2 border-primary/60 bg-muted/40 px-5 py-4 text-sm italic leading-relaxed text-muted-foreground">
            {block.text}
          </p>
        </Reveal>
      );
    case "banner":
      return (
        <Reveal>
          <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/[0.07] px-6 py-4">
            <Sparkles className="h-4 w-4 flex-shrink-0 text-primary" />
            <span className="font-serif text-base text-foreground">{block.text}</span>
          </div>
        </Reveal>
      );
    case "callout":
      return (
        <Reveal>
          <div className="luxury-card p-7 sm:p-9">
            <h3 className="font-serif text-2xl text-foreground">{block.title}</h3>
            <div className="section-divider ml-0 my-4" />
            <p className="leading-relaxed text-muted-foreground">{block.text}</p>
          </div>
        </Reveal>
      );
    case "cards":
      return (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {block.items.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 90}>
              <div className="luxury-card group h-full p-7">
                <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/20">
                  <Icon name={c.icon} className="h-5 w-5 text-primary" />
                </span>
                <h4 className="font-serif text-lg text-foreground">{c.title}</h4>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      );
    case "figure":
      return (
        <Reveal>
          <figure>
            <Placeholder src={block.img} label={block.caption} ratio={block.ratio || "16/9"} />
            {block.caption ? (
              <figcaption className="mt-3 text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {block.caption}
              </figcaption>
            ) : null}
          </figure>
        </Reveal>
      );
    case "gallery":
      return <Gallery items={block.items} />;
    case "rates":
      return <RatesTable rows={block.rows} note={block.note} venueName={venueName} />;
    case "ratesCustom":
      return (
        <Reveal>
          <RatesCustom title={block.title} columns={block.columns} rows={block.rows} />
        </Reveal>
      );
    case "priceCard":
      return (
        <Reveal>
          <div className="luxury-card flex flex-col gap-5 p-7 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="font-serif text-4xl text-primary">{block.price}</span>
              <span className="ml-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {block.unit}
              </span>
            </div>
            <ul className="space-y-2">
              {block.lines.map((l) => (
                <li key={l} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" />
                  {l}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      );
    case "reviews":
      return <Reviews items={block.items} />;
    case "faq":
      return (
        <Reveal>
          <div className="luxury-card p-7 sm:p-9">
            {block.title ? (
              <h3 className="mb-7 font-serif text-2xl text-foreground">{block.title}</h3>
            ) : null}
            <dl className="space-y-7">
              {block.items.map((f, i) => (
                <div
                  key={i}
                  className={i !== block.items.length - 1 ? "border-b border-border/50 pb-7" : ""}
                >
                  <dt className="font-serif text-lg text-accent">{f.q}</dt>
                  <dd className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
      );
    case "disclaimer":
      return (
        <Reveal>
          <div className="rounded-xl border border-border/60 bg-muted/30 p-7">
            <div className="mb-4 flex items-center gap-3">
              <Scale className="h-4 w-4 text-primary" />
              <h4 className="text-[11px] uppercase tracking-[0.28em] text-primary">
                {block.title}
              </h4>
            </div>
            <ul className="space-y-3">
              {block.items.map((it, i) => (
                <li key={i} className="text-xs leading-relaxed text-muted-foreground">
                  {it}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      );
    default:
      return null;
  }
};

export const Blocks = ({ blocks, venueName }) => (
  <div className="space-y-7">
    {blocks.map((b, i) => (
      <Block key={i} block={b} venueName={venueName} />
    ))}
  </div>
);

export { X };
export default Blocks;
